import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import PdfPreview from "../components/PdfPreview";
import { AuditBlock } from "../components/SignOff";
import { apiGet } from "../api";
import { exportDelivery, deliveryCertFile } from "../export";
import { formatDateTime } from "../dateutil";
import { C, R, S } from "../theme";

const AGREE = [
  ["agree_wind", "Wind Speed Limit — deflate immediately at 24 mph / 38 km/h"],
  ["agree_supervision", "Supervision & Rules — constant adult supervision, height/user limits, no shoes or sharp objects"],
  ["agree_no_relocation", "Strict No Relocation — unit anchored in place and will not be moved once set up"],
];

export default function DeliveryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => { apiGet(`/delivery-handovers/${id}`).then(setD).catch(() => {}); }, [id]);

  const onView = () => { try { const { file, ref } = deliveryCertFile(d); const url = URL.createObjectURL(file); setPreview({ file, url, name: `${ref}.pdf`, zoom: 1 }); } catch (e) { /* ignore */ } };
  const closePreview = () => setPreview((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });
  const setZoom = (dz) => setPreview((p) => (p ? { ...p, zoom: Math.min(3, Math.max(0.5, +(p.zoom + dz).toFixed(2))) } : p));
  const onDownload = () => { try { exportDelivery(d); } catch (e) { /* ignore */ } };
  const onShare = async () => {
    try {
      const { file } = deliveryCertFile(d);
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: "Delivery Handover Certificate" }); }
      else { onDownload(); }
    } catch (e) { /* AbortError = user cancelled */ }
  };

  if (!d) return <div style={{ color: C.amber, textAlign: "center", paddingTop: 80 }}>Loading…</div>;

  const card = { background: C.card, borderRadius: R.md, padding: S.lg, marginBottom: S.md, border: `1px solid ${C.border}` };
  const meta = (lbl, val) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: S.md, padding: `${S.sm}px 0`, borderBottom: `1px solid ${C.border}` }}>
      <span style={{ color: C.muted, fontSize: 13 }}>{lbl}</span>
      <span style={{ color: C.text, fontSize: 15, fontWeight: 700, textAlign: "right" }}>{val || "-"}</span>
    </div>
  );
  const btn = (bg, brd, span) => ({ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7, background: bg, border: brd, borderRadius: R.md, minHeight: 84, cursor: "pointer", ...(span ? { gridColumn: "1 / -1" } : {}) });

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg, paddingBottom: S.sm }}>
        <button data-testid="delivery-detail-back" className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>{d.attraction_name}</div>
          <div style={{ color: C.blue, fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>Delivery Handover</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, background: C.card2, padding: "4px 8px", borderRadius: R.pill, flexShrink: 0 }}><Icon name="lock-closed" size={12} color={C.green} /><span style={{ color: C.green, fontSize: 11, fontWeight: 800 }}>Immutable</span></div>
      </div>

      <div style={{ padding: S.lg, paddingTop: 0 }}>
        <div style={card}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.sm }}>Handover Details</div>
          {meta("Customer", d.customer_name)}
          {meta("Customer Email", d.customer_email)}
          {meta("Delivered By", d.delivered_by || d.driver_name)}
          {meta("Surface Type", d.surface_type)}
          {meta("Handover Date", formatDateTime(d.submitted_at))}
          {meta("Receipt Emailed", d.email_sent ? "Yes" : "No")}
        </div>

        {d.pre_existing_notes ? (
          <div style={card}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.sm }}>Pre-Existing Damage / Notes</div>
            <div style={{ color: C.sub, fontSize: 14, lineHeight: 1.5 }}>{d.pre_existing_notes}</div>
          </div>
        ) : null}

        <div style={card}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.sm }}>Safety Agreements Accepted by Hirer</div>
          {AGREE.map(([k, t]) => (
            <div key={k} style={{ display: "flex", gap: S.sm, alignItems: "flex-start", marginBottom: S.sm }}>
              <Icon name="checkmark-circle" size={18} color={d[k] ? C.green : C.muted} />
              <span style={{ color: C.sub, fontSize: 13, lineHeight: 1.4, flex: 1 }}>{t}</span>
            </div>
          ))}
        </div>

        {d.setup_photo ? (
          <div style={card}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: S.sm }}>Proof of Setup</div>
            <img data-testid="delivery-detail-photo" src={d.setup_photo} alt="Setup proof" style={{ width: "100%", borderRadius: R.md, display: "block" }} />
          </div>
        ) : null}

        <div style={{ marginBottom: S.md }}>
          <AuditBlock signature={d.driver_signature} name={d.delivered_by || d.driver_name} initials={d.driver_initials} submittedAt={d.submitted_at} label="Staff / Driver Sign-Off" />
        </div>
        <div style={{ marginBottom: S.md }}>
          <AuditBlock signature={d.customer_signature} name={d.customer_name} initials="" submittedAt={d.submitted_at} label="Customer Sign-Off" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: S.sm, marginTop: S.sm }}>
          <button data-testid="delivery-view-pdf" className="pressable" onClick={onView} style={btn(C.card2, `1px solid ${C.borderStrong}`)}>
            <Icon name="eye" size={24} color={C.amber} /><span style={{ color: C.text, fontWeight: 800, fontSize: 14 }}>View PDF</span>
          </button>
          <button data-testid="delivery-save-device" className="pressable" onClick={onDownload} style={btn(C.card2, `1px solid ${C.borderStrong}`)}>
            <Icon name="download" size={24} color={C.amber} /><span style={{ color: C.text, fontWeight: 800, fontSize: 14 }}>Save to Device</span>
          </button>
          <button data-testid="delivery-share" className="pressable" onClick={onShare} style={btn(C.amber, "none", true)}>
            <Icon name="paper-plane" size={24} color={C.black} /><span style={{ color: C.black, fontWeight: 900, fontSize: 15 }}>Share Certificate</span>
          </button>
        </div>
        <div style={{ height: S.xxl }} />
      </div>

      {preview && (
        <div className="modal-backdrop" style={{ zIndex: 10000, padding: 0 }} onClick={closePreview}>
          <div data-testid="delivery-pdf-modal" onClick={(e) => e.stopPropagation()} style={{ position: "fixed", inset: 0, background: C.bg, display: "flex", flexDirection: "column", overscrollBehavior: "contain" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: S.sm, paddingTop: "calc(env(safe-area-inset-top, 16px) + 12px)", paddingLeft: S.md, paddingRight: S.md, paddingBottom: S.md, background: C.card, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <div style={{ color: C.text, fontWeight: 900, fontSize: 16, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Certificate Preview</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <button className="pressable" onClick={() => setZoom(-0.25)} style={{ width: 40, height: 40, borderRadius: R.pill, background: C.card2, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 22, fontWeight: 900, lineHeight: 1 }}>−</button>
                <button className="pressable" onClick={() => setZoom(0.25)} style={{ width: 40, height: 40, borderRadius: R.pill, background: C.card2, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 22, fontWeight: 900, lineHeight: 1 }}>+</button>
                <button data-testid="delivery-pdf-close-top" className="pressable" onClick={closePreview} aria-label="Close preview" style={{ width: 44, height: 44, borderRadius: R.pill, background: C.amber, border: "none", color: C.black, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 8 }}><Icon name="close" size={24} color={C.black} /></button>
              </div>
            </div>
            <PdfPreview file={preview.file} zoom={preview.zoom} />
            <div style={{ padding: S.md, paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)", background: C.card, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
              <button data-testid="delivery-pdf-close" className="pressable" onClick={closePreview} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: C.amber, border: "none", color: C.black, borderRadius: R.pill, minHeight: 56, fontWeight: 900, fontSize: 17 }}><Icon name="close" size={24} color={C.black} /> Close Preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
